import React, {
  useCallback,
  useEffect,
  useMemo,
  ReactNode,
  RefObject,
  useRef,
} from 'react';
import compact from 'lodash/compact';
import clsx from 'clsx';
import CircleIcon from '@mui/icons-material/CircleRounded';
import AddIcon from '@mui/icons-material/Add';
import IconButton from '@mui/material/IconButton';
import {
  SelectClasses,
} from '@mui/material/Select';
import {
  InsuranceDTO,
} from 'dtos';
import EditIcon from 'core/icons/edit';
import {
  useGetPatientInsurances,
  useInsuranceMetaData,
} from 'views/EMR/components/Insurance/hooks';
import {
  insurancesEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import Select from 'core/components/Select';
import ToolTip from 'core/components/Tooltip';
import isNil from 'lodash/isNil';
import {
  useNotify,
} from 'core/hooks';
import {
  getAddressString,
} from 'utils/string';
import {
  getInsuranceBasicInfo,
} from 'utils/misc';

const parts = ['root', 'value', 'rootContainer'] as const;
type Part = typeof parts[number];

type Props = {
  patientId: number;
  insuranceId?: number;
  insuranceName?: string;
  className?: string;
  classes?: Partial<SelectClasses & Record<Part, string>>;
  placeholder?: string;
  disabled?: boolean;
  editable?: boolean;
  onChange: (option: InsuranceDTO | InsuranceDTO[]) => void;
  getLabel?: (option: InsuranceDTO, index?: number) => string | ReactNode;
  containerRef?: RefObject<HTMLElement>;
  hasDefault?: boolean;
  detailedView?: boolean;
  invalid?: boolean;
  invalidTooltip?: string;
  fitContent?: boolean;
  showInsuranceSlot?: boolean;
};

type InsuranceLabelProps = {
  option: InsuranceDTO;
  additionalTag?: ReactNode;
};

export function InsuranceLabel({
  option,
  additionalTag,
}: InsuranceLabelProps) {
  const {
    getInsuranceSlotLabel,
  } = useInsuranceMetaData();

  const isNotSet = (option?.insuranceId ?? 0) === 0;
  const insuranceSlotLabel = !isNotSet && !isNil(option?.insuranceSlot)
    ? getInsuranceSlotLabel(option.insuranceSlot)
    : null;

  const basicInfo = useMemo(
    () => getInsuranceBasicInfo(option?.payerEntry ?? {}),
    [option?.payerEntry],
  );

  return (
    <div className="flex flex-col gap-0 w-full min-h-[40px] justify-center">
      <div className="flex items-center gap-1.5">
        {!isNotSet && (
          <ToolTip title={option.inactive ? 'Inactive' : 'Active'} placement="top">
            <CircleIcon
              className={clsx('!h-2 !w-2 shrink-0', {
                'text-red-500': option.inactive ?? false,
                'text-emerald-700': !(option.inactive ?? false),
              })}
            />
          </ToolTip>
        )}
        <span className="whitespace-normal text-xs font-semibold text-gray-700">{option.insuranceName ?? ''}</span>
        {!isNotSet && !isNil(additionalTag) && additionalTag}
      </div>
      {!isNotSet && (
        <>
          <div className="flex items-center gap-1 pl-3.5">
            {!isNil(insuranceSlotLabel) && (
              <>
                <span className="!text-xs !font-semibold text-gray-500">{insuranceSlotLabel}</span>
                <span className="!text-xs text-gray-500">{' • '}</span>
              </>
            )}
            <span className="!text-xs text-gray-500">{basicInfo}</span>
          </div>
          {!isNil(option.payerEntry?.address) && (
            <div className="!text-xs text-gray-500 pl-3.5">{getAddressString(option.payerEntry?.address)}</div>
          )}
        </>
      )}
    </div>
  );
}

function InsuranceDetail({
  insuranceName,
  payerEntry,
}: InsuranceDTO) {
  const insuranceNameRef = useRef<HTMLDivElement>(null);
  const addressRef = useRef<HTMLDivElement>(null);

  const address = useMemo(() => getAddressString(payerEntry?.address), [payerEntry?.address]);
  const basicInfo = useMemo(() => getInsuranceBasicInfo(payerEntry ?? {}), [payerEntry]);

  return (
    <div className="flex flex-col w-full">
      <ToolTip title={insuranceName} placement="top" ref={insuranceNameRef} auto>
        <span className="w-full truncate font-semibold" ref={insuranceNameRef}>{insuranceName}</span>
      </ToolTip>
      <small>
        {basicInfo}
      </small>
      {!isNil(payerEntry?.address) && (
        <ToolTip title={address} placement="top" ref={addressRef} auto>
          <small>
            {address}
          </small>
        </ToolTip>
      )}
    </div>
  );
}

export default function PatientInsuranceSelect({
  patientId,
  insuranceId: currentInsuranceId,
  insuranceName: currentInsuranceName,
  className,
  onChange,
  disabled = false,
  placeholder = 'Not Set',
  classes,
  editable = true,
  getLabel: _getLabel,
  containerRef,
  hasDefault = true,
  detailedView = false,
  invalid = false,
  invalidTooltip = 'Insurance is invalid',
  fitContent = false,
  showInsuranceSlot = true,
}: Props) {
  const { getInsuranceSlotLabel } = useInsuranceMetaData();
  const {
    data: insuranceList,
    isFetching,
    refetch,
  } = useGetPatientInsurances(patientId);

  const options = useMemo(() => compact([
    hasDefault ? { insuranceName: 'Not Set', insuranceId: 0 } : null,
    ...(insuranceList ?? []),
  ]), [insuranceList]);

  const selected = useMemo(() => options.find(
    ({
      insuranceId,
      insuranceName,
    }) => currentInsuranceId === insuranceId || insuranceName === currentInsuranceName,
  ), [currentInsuranceId, currentInsuranceName, options]);

  const hasSelectedInsurance = (selected?.insuranceId ?? 0) !== 0;
  const selectedValueRef = useRef<HTMLDivElement>(null);

  const openInsuranceDialog = useCallback((insurance?: InsuranceDTO) => {
    insurancesEventChannel.emit('openInsuranceDialog', {
      insurance,
      patientId,
      dialogHeight: containerRef?.current?.offsetHeight,
    });
  }, [patientId]);

  useEffect(() => {
    const unsubscribeUpdated = insurancesEventChannel.on('onPatientInsuranceUpdated', async (_patientId, _insurance) => {
      if (_patientId === patientId) {
        await refetch();
        onChange(_insurance);
      }
    });

    const unsubscribeRefresh = insurancesEventChannel.on('onRefreshPatientInsurance', async (_patientId) => {
      if (_patientId === patientId) {
        await refetch();
      }
    });

    return () => {
      unsubscribeUpdated();
      unsubscribeRefresh();
    };
  }, [patientId]);

  const defaultGetLabel = useCallback(
    (option: InsuranceDTO) => (<InsuranceLabel option={option} />),
    [],
  );

  const getLabel = _getLabel ?? defaultGetLabel;

  const notify = useNotify();

  const handleInsuranceChange = (option: InsuranceDTO | InsuranceDTO[]) => {
    if ((option as InsuranceDTO)?.inactive) {
      notify({
        message: 'Selected insurance is inactive',
        variant: 'warning',
      });
    }
    onChange(option);
  };

  const selectedInsuranceDisplay = useMemo(() => (
    <div className="flex items-center justify-between w-full gap-1 min-w-0">
      <span className="truncate">{selected?.insuranceName ?? placeholder}</span>
      {showInsuranceSlot && !isNil(selected?.insuranceSlot) && (
        <span className="inline-flex shrink-0 items-center !bg-indigo-100 !text-indigo-700 rounded-full px-1.5 py-0.5 text-[10px] font-medium">
          {getInsuranceSlotLabel(selected!.insuranceSlot)}
        </span>
      )}
    </div>
  ), [getInsuranceSlotLabel, placeholder, selected, showInsuranceSlot]);

  return (
    <div className={clsx('flex min-w-0 max-w-full flex-1 items-center', classes?.rootContainer)}>
      <Select<InsuranceDTO>
        className={clsx({
          '!w-[calc(100%-76px)] mr-1': editable && !fitContent,
          'w-full': !editable && !fitContent,
          '!w-fit min-w-[220px] max-w-[360px] mr-1': editable && fitContent,
          'w-fit': !editable && fitContent,
        }, className)}
        muiClasses={{
          ...classes,
          root: clsx(classes?.root, { '!bg-red-50': invalid }),
          select: clsx(classes?.select, { '!bg-red-50': invalid }),
        }}
        id="patient-insurance"
        value={selected}
        options={options}
        placeholder={placeholder}
        getValue={(option) => option.insuranceId ?? 0}
        getLabel={getLabel}
        disabled={disabled}
        onChange={handleInsuranceChange}
        menuItemClassName="!border-b !border-b-gray-100 !border-solid last:!border-b-0"
        renderValue={() => (detailedView ? (
          <InsuranceDetail {...selected} />
        ) : (
          <ToolTip title={invalidTooltip} placement="top" hide={!invalid}>
            <div className={clsx('w-full flex items-center justify-start gap-1', classes?.value ?? '')}>
              {invalid && !hasSelectedInsurance && (
                <CircleIcon className="!h-2.5 !w-2.5 shrink-0 !text-red-600" />
              )}
              {!isNil(selected?.inactive) && (
                <CircleIcon className={clsx('!h-2.5 !w-2.5', {
                  'text-red-600': selected?.inactive ?? false,
                  'text-emerald-700': !selected?.inactive,
                })}
                />
              )}
              <ToolTip
                title={selected?.insuranceName ?? placeholder}
                placement="top"
                ref={selectedValueRef}
                auto={!invalid}
                hide={invalid}
              >
                <div className="min-w-0 flex-1" ref={selectedValueRef}>
                  {selectedInsuranceDisplay}
                </div>
              </ToolTip>
            </div>
          </ToolTip>
        ))}
      />
      {editable && (
        <>
          <ToolTip
            title="Update Selected Insurance"
            hide={!hasSelectedInsurance}
          >
            <span>
              <IconButton
                disabled={!hasSelectedInsurance || isFetching}
                onClick={() => { openInsuranceDialog(selected ?? {}); }}
                className={clsx({
                  '!pointer-events-auto !cursor-not-allowed': !hasSelectedInsurance || isFetching,
                }, 'nw-edit-insurance-button')}
              >
                <EditIcon
                  className={clsx('!w-5 !h-5 !text-inherit', {
                    '!opacity-60': !hasSelectedInsurance,
                  })}
                />
              </IconButton>
            </span>
          </ToolTip>
          <ToolTip title="Add Insurance">
            <span>
              <IconButton
                disabled={isFetching}
                onClick={() => { openInsuranceDialog(); }}
                className="nw-add-insurance-button"
              >
                <AddIcon className="!w-5 !h-5 text-neutral-600" />
              </IconButton>
            </span>
          </ToolTip>
        </>
      )}
    </div>
  );
}
